import React, { useEffect } from 'react';
import insightsSocket from 'sockets/insightsSocketService';
import { useAuth } from 'contexts/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from 'utils/apiUtils';

class EventTrackingService {
  constructor() {
    this.authContext = null;
    this.connected = false;
  }

  // Initialize with auth context
  init(authContext) {
    this.authContext = authContext;

    // Connect socket if we have a user ID
    if (this.authContext?.user?.userId) {
      this.connectSocket(this.authContext.user.userId);
    }
  }

  // Connect socket explicitly
  connectSocket(userId) {
    if (userId && !this.connected) {
      console.log('EventTrackingService: Connecting socket with userId:', userId);
      insightsSocket.connect(userId);
      this.connected = insightsSocket.connected;
    }
  }

  // Get current user ID
  getUserId() {
    return this.authContext?.user?.userId;
  }

  // Environment detection function
  isProduction() {
    // Check if we're running on production/staging
    return window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('production') ||
      window.location.hostname !== 'localhost';
  }

  // Determine tracking strategy based on environment
  getTrackingStrategy() {
    // ✅ FIXED: Prefer WebSockets for better real-time performance
    // Use WebSockets in all environments now that they're properly configured
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Always prefer WebSockets for better real-time performance
    return 'socket';
  }
  // Main tracking method with environment-specific strategy
  trackCardView(cardId, sourceType = 'share_link') {
    const userId = this.getUserId();

    console.log('TrackCardView called:', { cardId, sourceType, userId });

    if (!cardId) {
      console.warn('Cannot track: missing cardId', { cardId });
      return;
    }

    // Normalize sourceType for consistency
    let normalizedSourceType = sourceType;
    if (sourceType === 'website' || sourceType === 'direct' || !sourceType) {
      normalizedSourceType = 'share_link';
    } else if (sourceType === 'nfc' || sourceType === 'tag') {
      normalizedSourceType = 'accessory';
    }

    if (normalizedSourceType !== sourceType) {
      console.log(`Normalized source type from ${sourceType} to ${normalizedSourceType}`);
    }

    // Get preferred tracking strategy for current environment
    const strategy = this.getTrackingStrategy();
    console.log(`Using ${strategy} tracking strategy`);    // Track based on strategy
    if (strategy === 'http') {
      // In production/staging environment, use HTTP
      this.trackCardViewViaHTTP(cardId, normalizedSourceType);
    } else {
      // In local development, try WebSocket first with HTTP fallback
      try {
        // Ensure socket is connected
        if (userId) {
          this.connectSocket(userId);
        }

        if (userId && insightsSocket && insightsSocket.connected) {
          console.log('Using socket for tracking');
          insightsSocket.emitCardView({
            cardId,
            viewSource: normalizedSourceType,
            sourceType: normalizedSourceType,
            userId
          });
        } else {
          console.log('Socket not connected or no userId, using HTTP');
          this.trackCardViewViaHTTP(cardId, normalizedSourceType);
        }
      } catch (error) {
        console.error('Socket tracking error, using HTTP fallback:', error);
        this.trackCardViewViaHTTP(cardId, normalizedSourceType);
      }
    }
  }
  // HTTP tracking method for reliability
  trackCardViewViaHTTP(cardId, sourceType) {
    const userId = this.getUserId() || 'anonymous'; // Use 'anonymous' when no user ID

    console.group('Card View Tracking');
    console.log('Card ID:', cardId);
    console.log('Source Type:', sourceType);
    console.log('User ID:', userId);
    console.log('Timestamp:', new Date().toISOString());
    console.log('Is Anonymous:', userId === 'anonymous');
    console.groupEnd();

    // Normalize sourceType for consistency
    let normalizedSourceType = sourceType || 'share_link';
    if (normalizedSourceType === 'website' || normalizedSourceType === 'direct') {
      normalizedSourceType = 'share_link';
    } else if (normalizedSourceType === 'nfc' || normalizedSourceType === 'tag') {
      normalizedSourceType = 'accessory';
    }

    console.log(`Source type normalized from ${sourceType} to ${normalizedSourceType}`);

    // Determine the correct API endpoint based on your backend structure
    // Try multiple formats to ensure compatibility
    const endpoints = [
      `${API_BASE_URL}/api/insights/track-view`,
      `${API_BASE_URL}/api/insights/${userId}/track-view`,
      `${API_BASE_URL}/api/event-log/track`,
      `${API_BASE_URL}/api/insights/qr-track`  // This endpoint also works for accessory tracking
    ];

    // Get auth token if available
    const token = localStorage.getItem('authToken') ||
      localStorage.getItem('auth._token.local');

    // Create headers
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }    // Track data payload
    const payload = {
      userId,
      cardId,
      sourceType: normalizedSourceType,
      eventType: 'card_view',
      timestamp: new Date().toISOString(),
      isAnonymous: userId === 'anonymous'
    };

    console.log('Sending tracking payload:', payload);

    // Try each endpoint until one succeeds
    const tryEndpoint = (index) => {
      if (index >= endpoints.length) {
        console.error('All tracking endpoints failed');
        return;
      }

      fetch(endpoints[index], {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      })
        .then(response => {
          console.log(`Endpoint ${index} response:`, response.status);
          if (response.ok) {
            console.log('Tracking successful');
            return;
          }
          // Try next endpoint if this one failed
          tryEndpoint(index + 1);
        })
        .catch(error => {
          console.error(`Endpoint ${index} error:`, error);
          // Try next endpoint
          tryEndpoint(index + 1);
        });
    };

    // Start trying endpoints
    tryEndpoint(0);
  }
  // Link tap events with HTTP fallback
  trackLinkTap(linkData) {
    const userId = this.getUserId();

    console.log('TrackLinkTap called:', { linkData, userId });

    if (!linkData) {
      console.warn('Cannot track link tap: missing linkData');
      return;
    }

    // Get preferred tracking strategy for current environment
    const strategy = this.getTrackingStrategy();
    console.log(`Using ${strategy} tracking strategy for link tap`);

    // Track based on strategy
    if (strategy === 'http') {
      // In production/staging environment, use HTTP
      this.trackLinkTapViaHTTP(linkData);
    } else {
      // In local development, try WebSocket first with HTTP fallback
      try {
        // Ensure socket is connected
        if (userId) {
          this.connectSocket(userId);
        } if (userId && insightsSocket && insightsSocket.connected) {
          console.log('Using socket for link tap tracking');
          insightsSocket.emitLinkTap({
            ...linkData,
            userId
          });
        } else {
          console.log('Socket not connected or no userId, using HTTP for link tap');
          this.trackLinkTapViaHTTP(linkData);
        }
      } catch (error) {
        console.error('Socket link tap tracking error, using HTTP fallback:', error);
        this.trackLinkTapViaHTTP(linkData);
      }
    }
  }

  // HTTP tracking method for link taps
  trackLinkTapViaHTTP(linkData) {
    const userId = this.getUserId() || 'anonymous';
    const timestamp = new Date().toISOString();

    console.group('Link Tap Tracking');
    console.log('Link Type:', linkData.linkType);
    console.log('Card ID:', linkData.cardId);
    console.log('User ID:', userId);
    console.log('Timestamp:', timestamp);
    console.groupEnd();

    // Get auth token if available
    const token = localStorage.getItem('authToken') ||
      localStorage.getItem('auth._token.local');

    // Create headers
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }    // Streamlined payload with only essential data
    const payload = {
      userId,
      cardId: linkData.cardId,
      eventType: 'link_tap',
      sourceType: 'card',
      timestamp: timestamp,
      isAnonymous: userId === 'anonymous',
      metadata: {
        linkType: linkData.linkType,
        linkUrl: linkData.linkUrl,
        linkTitle: linkData.linkTitle || linkData.linkType,
        cardHolderName: linkData.cardHolderName, // Include cardHolderName in metadata
        timestamp: timestamp
      }
    }; console.log('Sending link tap tracking payload:', payload);

    // Use the existing track-view endpoint which handles link taps
    const endpoint = `${API_BASE_URL}/api/insights/track-view`;

    // Make a single attempt with the dedicated endpoint
    return fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (response.ok) {
          console.log('✅ Link tap tracking successful');
          return response;
        }
        throw new Error(`Link tap tracking failed: ${response.status}`);
      })
      .catch(error => {
        console.error('❌ Link tap tracking error:', error);
        throw error;
      });
  }

  // Lead capture events
  trackLeadCapture(leadData) {
    const userId = this.getUserId();
    if (userId && leadData) {
      try {
        // Ensure socket is connected
        this.connectSocket(userId);

        console.log('Tracking lead capture event:', { ...leadData, userId });
        insightsSocket.emitNewLead({
          ...leadData,
          userId
        });
      } catch (error) {
        console.error('Error tracking lead capture:', error);
      }
    } else {
      console.error('Cannot track lead: missing userId or leadData', { userId, leadData });
    }
  }



  trackAccessoryView(accessoryData) {
    const userId = this.getUserId();

    // Validate essential data
    if (!accessoryData) {
      console.warn('Cannot track accessory view: missing data');
      return;
    }

    // Get the serial number from the passed data
    const serialNumber = accessoryData.serialNumber;

    // If no serial number provided, log a warning but continue with tracking
    if (!serialNumber) {
      console.warn('Tracking accessory view without serial number:', accessoryData);
    } else {
      console.log('Tracking accessory view for serial:', serialNumber);
    }

    try {
      // Prepare tracking payload with the serial number (if available)
      const trackingPayload = {
        serialNumber: serialNumber || 'unknown',
        cardId: accessoryData.cardId,
        sourceType: 'accessory',
        userId: userId || 'anonymous',
        metadata: {
          accessorySerialNumber: serialNumber,
          exactSerialNumber: serialNumber,
          timestamp: new Date().toISOString(),
          isAccessoryScan: true,
          ...accessoryData.metadata
        }
      };

      // For clarity in logs
      console.log('ACCESSORY TRACKING:', {
        serialNumber: trackingPayload.serialNumber,
        cardId: trackingPayload.cardId,
        userId: trackingPayload.userId
      });

      // Multiple tracking methods for redundancy
      const trackingMethods = [
        // HTTP Tracking Method
        fetch(`${API_BASE_URL}/api/insights/accessory-track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(trackingPayload)
        }),

        // Direct Event Log Method as backup
        fetch(`${API_BASE_URL}/api/event-log/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...trackingPayload,
            entityId: serialNumber || trackingPayload.cardId,
            entityType: 'accessory',
            eventType: 'accessory_view'
          })
        })
      ];

      // Execute tracking methods
      Promise.allSettled(trackingMethods)
        .then(results => {
          const successfulTracks = results.filter(
            result => result.status === 'fulfilled' &&
              (result.value.ok || result.value.success)
          );

          console.log('Tracking Results:', {
            totalAttempts: results.length,
            successful: successfulTracks.length
          });
        })
        .catch(error => {
          console.error('Tracking execution error:', error);
        });
    } catch (error) {
      console.error('Critical error in accessory view tracking:', error);
    }
  }








  // Contact download events
  trackContactDownload(cardId) {
    const userId = this.getUserId();

    console.log('TrackContactDownload called:', { cardId, userId });

    if (!cardId) {
      console.warn('Cannot track: missing cardId', { cardId });
      return;
    }

    // Get preferred tracking strategy for current environment
    const strategy = this.getTrackingStrategy();
    console.log(`Using ${strategy} tracking strategy for contact download`);

    // Track based on strategy
    if (strategy === 'http') {
      // In production/staging environment, use HTTP
      this.trackContactDownloadViaHTTP(cardId);
    } else {
      // In local development, try WebSocket first with HTTP fallback
      try {
        // Ensure socket is connected
        if (userId) {
          this.connectSocket(userId);
        }

        if (userId && insightsSocket && insightsSocket.connected) {
          console.log('Using socket for tracking contact download');
          insightsSocket.emitContactDownload({
            cardId,
            userId
          });
        } else {
          console.log('Socket not connected or no userId, using HTTP for contact download');
          this.trackContactDownloadViaHTTP(cardId);
        }
      } catch (error) {
        console.error('Socket tracking error, using HTTP fallback for contact download:', error);
        this.trackContactDownloadViaHTTP(cardId);
      }
    }
  }

  // Enhanced trackContactDownload method for reliability
  trackContactDownloadViaHTTP(cardId) {
    const userId = this.getUserId() || 'anonymous'; // Use 'anonymous' when no user ID

    console.group('Contact Download Tracking');
    console.log('Card ID:', cardId);
    console.log('User ID:', userId);
    console.log('Timestamp:', new Date().toISOString());
    console.log('Is Anonymous:', userId === 'anonymous');
    console.groupEnd();

    // Track data payload with better metadata
    const payload = {
      userId,
      cardId,
      entityId: cardId,
      entityType: 'card',
      eventType: 'contact_download',
      sourceType: 'card',
      timestamp: new Date().toISOString(),
      isAnonymous: userId === 'anonymous',
      metadata: {
        timestamp: new Date().toISOString(),
        action: 'contact_download'
      }
    };

    console.log('Sending contact download tracking payload:', payload);

    // Try multiple endpoints for better success rate
    const endpoints = [
      `${API_BASE_URL}/api/event-log/create`,
      `${API_BASE_URL}/api/event-log/track`,
      `${API_BASE_URL}/api/insights/event-log/create`,
      `${API_BASE_URL}/api/insights/${userId}/track-view` // This endpoint can also handle events
    ];

    // Try each endpoint until one succeeds
    const tryEndpoint = (index) => {
      if (index >= endpoints.length) {
        console.error('All contact download tracking endpoints failed');
        return;
      }

      fetch(endpoints[index], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(response => {
          console.log(`Contact download tracking response from endpoint ${index}:`, response.status);
          if (response.ok) {
            console.log('Contact download tracking successful');
            return;
          }
          // Try next endpoint if this one failed
          tryEndpoint(index + 1);
        })
        .catch(error => {
          console.error(`Contact download tracking error with endpoint ${index}:`, error);
          // Try next endpoint
          tryEndpoint(index + 1);
        });
    };

    // Start trying endpoints
    tryEndpoint(0);
  }





  // Share events with improved WhatsApp tracking
  trackShare(shareData) {
    const userId = this.getUserId();
    if (userId && shareData) {
      try {
        // Ensure socket is connected
        this.connectSocket(userId);

        console.group('Share Tracking');
        console.log('Share Type:', shareData.shareType);
        console.log('Card ID:', shareData.cardId);
        console.log('User ID:', userId);
        console.log('Timestamp:', new Date().toISOString());
        console.groupEnd();

        // Implement HTTP-based tracking for reliability
        this.trackShareViaHTTP(shareData);

        // Also emit via socket if connected
        if (insightsSocket && insightsSocket.connected) {
          insightsSocket.socket.emit('shareTracked', {
            ...shareData,
            userId,
            timestamp: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('Error tracking share:', error);
        // Try HTTP method as fallback
        this.trackShareViaHTTP(shareData);
      }
    } else {
      console.warn('Cannot track share: missing userId or shareData', { userId, shareData });
    }
  }

  // HTTP tracking method for shares with WhatsApp support
  trackShareViaHTTP(shareData) {
    const userId = this.getUserId() || 'anonymous';
    const timestamp = new Date().toISOString();

    // Create headers
    const headers = {
      'Content-Type': 'application/json'
    };

    const token = localStorage.getItem('authToken') ||
      localStorage.getItem('auth._token.local');

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Prepare payload
    const payload = {
      userId,
      cardId: shareData.cardId,
      shareType: shareData.shareType,
      timestamp: timestamp
    };

    console.log('Sending share tracking payload:', payload);

    // Use the card API endpoint for tracking shares
    return fetch(`${API_BASE_URL}/api/card/share/${shareData.cardId}`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (response.ok) {
          console.log('✅ Share tracking successful');
          return response;
        }
        throw new Error(`Share tracking failed: ${response.status}`);
      })
      .catch(error => {
        console.error('❌ Share tracking error:', error);
      });
  }

  // Generic event tracking
  trackEvent(eventType, eventData) {
    const userId = this.getUserId();
    if (userId && eventType) {
      try {
        // Ensure socket is connected
        this.connectSocket(userId);

        // Fallback method for custom events
        insightsSocket.socket.emit(eventType, {
          ...eventData,
          userId,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error(`Error tracking event ${eventType}:`, error);
      }
    } else {
      console.warn('Cannot track custom event: missing userId or eventType', { userId, eventType });
    }
  }
}

// Create a singleton instance
const eventTrackingService = new EventTrackingService();

// Custom hook for easy usage in React components
export const useEventTracking = () => {
  const { authState } = useAuth();

  // Initialize service with current auth context
  useEffect(() => {
    if (authState && authState.user) {
      console.log('Initializing event tracking service with user:', authState.user.userId);
      eventTrackingService.init(authState);
    }
  }, [authState]);

  return eventTrackingService;
};

export default eventTrackingService;